import { Suspense } from 'react';
import { Pivot, PivotItem } from '@fluentui/react';
import {
  AdvisoryList,
  DeploymentList,
  UpdateList,
  VulnerabilityList,
} from '..';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { AppErrorBoundary } from '@msrceng/component';
import { supportEmailUrl } from 'config/shared';
import { useFlagAdvisory } from 'feature/advisory/useFlagAdvisory';
import { SearchDetailsListShimmer } from 'component/Search';

/**
 * This is a list of tabs and their relative path.
 */
const tabList = {
  updateList: '/',
  deploymentList: '/deployments',
  vulnerabilityList: '/vulnerability',
  acknowledgement: '/acknowledgement',
  advisoryList: '/advisory',
} as const;

export const SearchResultTabPanel = () => {
  const { t } = useTranslation('home');
  const history = useHistory();
  const { pathname } = useLocation();
  const isAdvisoryEnabled = useFlagAdvisory();
  const showAdvisoryTab =
    isAdvisoryEnabled || pathname.includes(tabList.advisoryList);

  return (
    <>
      <Pivot
        linkSize='normal'
        selectedKey={pathname}
        onLinkClick={item =>
          item?.props?.itemKey && history.push(item.props.itemKey)
        }
        headersOnly={true}
        data-testid='tab-panel-pivot'
        styles={{
          linkContent: {
            minWidth: '5em',
          },
        }}
        overflowBehavior='menu'
      >
        <PivotItem
          headerText={t('home:tabTitle:Updates', 'Updates')}
          itemKey={tabList.updateList}
          data-testid='tab-panel-pivot-update'
        />
        <PivotItem
          headerText={t('home:tabTitle:Deployments', 'Deployments')}
          itemKey={tabList.deploymentList}
          data-testid='tab-panel-pivot-deployment'
        />
        <PivotItem
          headerText={t('home:tabTitle:Vulnerabilities', 'Vulnerabilities')}
          itemKey={tabList.vulnerabilityList}
          data-testid='tab-panel-pivot-vulnerability'
        />
        {showAdvisoryTab && (
          <PivotItem
            headerText={t('home:tabTitle:Advisories', 'Advisories')}
            itemKey={tabList.advisoryList}
            data-testid='tab-panel-pivot-advisory'
          />
        )}
        {/* <PivotItem
            headerText={t('home:tabTitle:Release Notes')}
            itemKey={tabList.releaseNote}
          /> */}
      </Pivot>
      {/* Let the user change tabs back if for some reason the js is messed up on a single tab */}
      <AppErrorBoundary
        {...{
          supportEmailUrl,
        }}
      >
        <Suspense
          fallback={<SearchDetailsListShimmer tableName={'suspended'} />}
        >
          <SearchResultTabContentSelector pathname={pathname} />
        </Suspense>
      </AppErrorBoundary>
    </>
  );
};

const SearchResultTabContentSelector = ({ pathname }: { pathname: string }) => {
  switch (pathname) {
    case tabList.deploymentList:
      return <DeploymentList />;
    case tabList.vulnerabilityList:
      return <VulnerabilityList />;
    case tabList.advisoryList:
      return <AdvisoryList />;
    default:
    case tabList.updateList:
      return <UpdateList />;
  }
};
